import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@gettactic/components';
import { TextInput } from '@gettactic/components/src/form/TextInput';
import {
  Database,
  MoreHorizontal,
  Pencil,
  Save,
  Upload,
  X
} from 'lucide-react';
import React from 'react';
import { MapBuilderProps } from '../../../../MapBuilder';
import toast from 'react-hot-toast';
import { SpotShape } from '../../../../types';
import { ZoomControls } from '../../../ZoomControls';
import { useMapCanvas } from '../../contexts/MapCanvasContext';
import { useMapEditor } from '../../contexts/MapEditorContext';
import { EDITING_ZONE_MAX_HEIGHT, EDITING_ZONE_MAX_WIDTH } from '../../defines';
import { getWindowKonvaObject } from '../../helpers/konva/getWindowKonvaObject';
import { useZoomStageToScale } from '../../helpers/useZoomStageToScale';
import { useUploadFloorPlan } from '../helpers/uploadFloorPlan';
import { SpotsManager } from '../spots/SpotsManager';
import { NumberingPreviewTool } from './tools/NumberingPreviewTool';
import { Switch } from '@gettactic/components/src/@v2/components/ui/Switch';

const classes = {
  Wrapper:
    'flex justify-between bg-background-primary w-full border-b-4 border-b-gray-200 items-center',
  Button:
    'flex items-center gap-1 p-3 hover:bg-gray-100 transition-colors text-gray-700',
  RightWrapper: 'flex gap-2 items-center'
};

export const TopToolbar: React.FC = () => {
  const mapCanvas = useMapCanvas();
  const floorInputRef = React.useRef<HTMLInputElement>(null);
  const backgroundInputRef = React.useRef<HTMLInputElement>(null);
  const { stagePosition, centerStage, shapes } = mapCanvas;
  const {
    setEditingResources,
    selectedIds,
    editingResources,
    snapToGrid,
    setSnapToGrid,
    save: handleSave
  } = useMapEditor();
  const uploadFloorPlan = useUploadFloorPlan();

  const handleZoom = useZoomStageToScale();

  const handleZoomIn = () => {
    const newScale = Math.min(stagePosition.scale * 1.2, 5);
    handleZoom(getWindowKonvaObject().stages[0], newScale, true);
  };

  const handleZoomOut = () => {
    const newScale = Math.max(stagePosition.scale / 1.2, 0.1);
    handleZoom(getWindowKonvaObject().stages[0], newScale, true);
  };

  const [isOpen, setIsOpen] = React.useState(false);

  const handleSetEditingResources = () => {
    if (editingResources?.isOpen) {
      setEditingResources({
        isOpen: false,
        resourceIds: []
      });
      return;
    }
    const resourceIds = selectedIds
      .filter((id) => {
        const shape = shapes.find((shape) => shape.id === id);
        return shape?.type === 'spot' && shape?.resource_id;
      })
      .map(
        (id) =>
          (shapes.find((shape) => shape.id === id) as SpotShape | undefined)
            ?.resource_id
      ) as string[];

    setEditingResources({
      isOpen: true,
      resourceIds
    });
  };

  const handleFloorFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    try {
      const file = e.target.files?.[0];
      await uploadFloorPlan(file);
      if (floorInputRef.current) {
        floorInputRef.current.value = '';
      }
    } catch (error) {
      toast.error('Error uploading floor plan');
    }
  };

  const handleBackgroundFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    try {
      const file = e.target.files?.[0];
      const extension = file?.name.split('.').pop();
      await uploadFloorPlan(file, {
        imageType: extension === 'jpg' || extension === 'jpeg' ? 'jpg' : 'png',
        type: 'background',
        replaceAll: false,
        resizeEditingZone: false
      });
      if (floorInputRef.current) {
        floorInputRef.current.value = '';
      }
    } catch (error) {
      toast.error('Error uploading background');
    }
  };

  return (
    <div className={classes.Wrapper}>
      <input
        ref={floorInputRef}
        type="file"
        accept=".svg"
        onChange={handleFloorFileChange}
        className="hidden"
      />
      <input
        ref={backgroundInputRef}
        type="file"
        accept=".png,.jpg,.jpeg"
        onChange={handleBackgroundFileChange}
        className="hidden"
      />
      <div className={'flex items-center text-sm'}>
        <button className={classes.Button} onClick={handleSave}>
          <Save size={20} />
          Save Map
        </button>
        <div className={'mx-6 h-4 w-px bg-secondary'} />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className={classes.Button}>
              <Upload size={20} /> Upload
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => floorInputRef.current?.click()}>
              Floor Plan
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => backgroundInputRef.current?.click()}
            >
              Background
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <div className={'mx-6 h-4 w-px bg-secondary'} />
        <div className={'flex items-center gap-2'}>
          <TextInput
            placeholder="W"
            className={'w-20'}
            value={String(mapCanvas.editingZoneWidth)}
            onChange={(e) => {
              mapCanvas.updateEditingZoneSize(
                Math.min(EDITING_ZONE_MAX_WIDTH, Number(e.target.value)),
                mapCanvas.editingZoneHeight
              );
            }}
          />
          -
          <TextInput
            placeholder="H"
            className={'w-20'}
            value={String(mapCanvas.editingZoneHeight)}
            onChange={(e) => {
              mapCanvas.updateEditingZoneSize(
                mapCanvas.editingZoneWidth,
                Math.min(EDITING_ZONE_MAX_HEIGHT, Number(e.target.value))
              );
            }}
          />
        </div>
        <div className={'mx-6 h-4 w-px bg-secondary'} />
        {/* biome-ignore lint/a11y/noLabelWithoutControl: <explanation> */}
        <label className={'flex items-center gap-2'}>
          <Switch checked={snapToGrid} onCheckedChange={setSnapToGrid} />
          Snap to grid
        </label>
      </div>
      <div>
        <div className={classes.RightWrapper}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className={classes.Button}>
                <MoreHorizontal size={20} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuGroup>
                <DropdownMenuItem onClick={() => setIsOpen(true)}>
                  <Database size={16} />
                  Manage Spots
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleSetEditingResources}
                  className="flex items-center gap-1"
                >
                  {!editingResources?.isOpen ? (
                    <>
                      <Pencil size={16} />
                      Edit resources
                    </>
                  ) : (
                    <>
                      <X size={16} />
                      Close Resources Editor
                    </>
                  )}
                </DropdownMenuItem>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>

          <NumberingPreviewTool />
          <ZoomControls
            scale={stagePosition.scale}
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            onCenter={centerStage}
          />
        </div>
      </div>
      <SpotsManager isOpen={isOpen} onOpenChange={setIsOpen} />
    </div>
  );
};
